import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Alert, Snackbar, CircularProgress } from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

const EnterpriseSystem = () => {
  const [form, setForm] = useState({
    nombre_sistema: "",
    ruc_proveedor_sistema: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const getToken = () => localStorage.getItem("access");

  const showMessage = (severity, message) => {
    setSnackbar({ open: true, severity, message });
  };

  const loadConfig = async () => {
    setLoading(true);

    try {
      const response = await fetch("/enterprise/empresa/configuracion-sistema/", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showMessage("error", data?.error || "No se pudo cargar la configuración.");
        return;
      }

      const config = data?.configuracion || {};

      setForm({
        nombre_sistema: config.nombre_sistema || "",
        ruc_proveedor_sistema: config.ruc_proveedor_sistema || "",
      });
    } catch {
      showMessage("error", "Error de conexión con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "ruc_proveedor_sistema") {
      setForm((prev) => ({
        ...prev,
        [name]: value.replace(/\D/g, "").slice(0, 13),
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const nombreSistema = form.nombre_sistema.trim();
    const rucProveedor = form.ruc_proveedor_sistema.trim();

    if (rucProveedor && !/^\d{13}$/.test(rucProveedor)) {
      showMessage("error", "El RUC del proveedor debe tener exactamente 13 dígitos.");
      return;
    }

    setSaving(true);

    try {
      const response = await fetch("/enterprise/empresa/configuracion-sistema/actualizar/", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nombre_sistema: nombreSistema,
          ruc_proveedor_sistema: rucProveedor,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        let message = "No se pudo guardar la configuración.";

        if (typeof data?.error === "string") {
          message = data.error;
        } else if (data?.error?.ruc_proveedor_sistema) {
          message = Array.isArray(data.error.ruc_proveedor_sistema)
            ? data.error.ruc_proveedor_sistema[0]
            : data.error.ruc_proveedor_sistema;
        }

        showMessage("error", message);
        return;
      }

      const config = data?.configuracion;

      if (config) {
        setForm({
          nombre_sistema: config.nombre_sistema || "",
          ruc_proveedor_sistema: config.ruc_proveedor_sistema || "",
        });
      }

      showMessage("success", data?.mensaje || "Configuración guardada correctamente.");
    } catch {
      showMessage("error", "Error de conexión con el servidor.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={34} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#1565C0", mb: 2 }}>
        Configuración del sistema
      </Typography>

      <Box sx={{ maxWidth: 850, display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          fullWidth
          label="Nombre del sistema"
          name="nombre_sistema"
          value={form.nombre_sistema}
          onChange={handleChange}
          placeholder="Ej. FACTUSYSDR"
          inputProps={{ maxLength: 150 }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiInputLabel-root.Mui-focused": { color: "#1565C0" },
            "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#1565C0" },
          }}
        />

        <TextField
          fullWidth
          label="RUC del proveedor del sistema"
          name="ruc_proveedor_sistema"
          value={form.ruc_proveedor_sistema}
          onChange={handleChange}
          placeholder="13 dígitos"
          inputProps={{ maxLength: 13, inputMode: "numeric" }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiInputLabel-root.Mui-focused": { color: "#1565C0" },
            "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#1565C0" },
          }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving
                ? <CircularProgress size={18} sx={{ color: "#fff" }} />
                : <SaveOutlinedIcon />
            }
            sx={{
              bgcolor: "#1565C0",
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              px: 3,
              minHeight: 42,
              "&:hover": { bgcolor: "#0D47A1" },
            }}
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnterpriseSystem;